import { api } from "./api";

/**
 * COCO Store — Zonal Head (Level-1 approval) assignment endpoints.
 *
 * A store's Level-1 approver is resolved from the store's state -> zone, where a
 * state-specific assignment wins over the zone-wide one. These endpoints manage
 * those assignments.
 *
 * The shared `api` instance's baseURL already ends in /api, so paths start at
 * /coco-store — matching coco-store-overview-api.js and coco-bulk-payment-api.js.
 */

// Drops empty query params so the request URL carries only what was actually set.
function buildParams(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((key) => {
    const value = obj[key];
    if (value === null || value === undefined || value === "") return;
    out[key] = value;
  });
  return out;
}

/** GET /coco-store/zonal-head/zones — fixed zone -> states map, for the dropdowns. */
export const zonalHeadZonesApi = () => api.get("/coco-store/zonal-head/zones");

/** GET /coco-store/zonal-head/list — zone, state and is_active are all optional. */
export const zonalHeadListApi = ({ zone, state, is_active } = {}) =>
  api.get("/coco-store/zonal-head/list", {
    params: buildParams({ zone, state, is_active }),
  });

/** GET /coco-store/zonal-head/:id */
export const zonalHeadByIdApi = (id) => api.get(`/coco-store/zonal-head/${id}`);

/**
 * POST /coco-store/zonal-head/assign
 *
 * `state` may be an array of states (one assignment per state), a single state
 * string, or null for a whole-zone assignment. Response data is
 * { created: [...], skipped: [...] } — states already assigned in the zone are
 * skipped rather than errored.
 */
export const assignZonalHeadApi = (params) =>
  api.post("/coco-store/zonal-head/assign", JSON.stringify(params));

/**
 * POST /coco-store/zonal-head/update
 *
 * `id` is required; user_id, zone, state and is_active are optional. `state`
 * here is a single state string, or null to convert to a whole-zone assignment.
 */
export const updateZonalHeadApi = (params) =>
  api.post("/coco-store/zonal-head/update", JSON.stringify(params));

/** DELETE /coco-store/zonal-head/:id — soft delete, sets is_active = false. */
export const deleteZonalHeadApi = (id) =>
  api.delete(`/coco-store/zonal-head/${id}`);

/** GET /coco-store/zonal-head/resolve — preview which head a state routes to. */
export const resolveZonalHeadApi = ({ state, pincode } = {}) =>
  api.get("/coco-store/zonal-head/resolve", {
    params: buildParams({ state, pincode }),
  });

/** Users for the Zonal Head picker — the same endpoint the User Creation list uses. */
export const zonalHeadUserListApi = () =>
  api.get(
    "/user/getAll?pageNumber=1&pageSize=500&sortBy=&sortOrder=&fromDate=&toDate=&role="
  );
